import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db, storage } from "../../firebase";
import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import {
    AlertTriangle,
    Camera,
    X,
    MapPin,
    Save,
    Check,
    Loader2,
    Link as LinkIcon
} from "lucide-react";

// Importamos el componente CategoryManager
import CategoryManager from "./CategoryManager";

const NewPlace = () => {
    const navigate = useNavigate();
    const { user } = useUserAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    // Slug validation state
    const [slugError, setSlugError] = useState("");
    const [checkingSlug, setCheckingSlug] = useState(false);
    const [slugAvailable, setSlugAvailable] = useState(null);
    const [slugTouched, setSlugTouched] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        address: "",
        phone: "",
        email: "",
        website: "",
        slug: "",
        categories: [],
        openingHours: {
            monday: { open: "08:00", close: "22:00", closed: false },
            tuesday: { open: "08:00", close: "22:00", closed: false },
            wednesday: { open: "08:00", close: "22:00", closed: false },
            thursday: { open: "08:00", close: "22:00", closed: false },
            friday: { open: "08:00", close: "22:00", closed: false },
            saturday: { open: "10:00", close: "23:00", closed: false },
            sunday: { open: "10:00", close: "18:00", closed: false },
        },
    });

    // Categories state - ahora manejado por CategoryManager
    const [allCategories, setAllCategories] = useState([]);

    // Image upload state
    const [images, setImages] = useState([]);
    const [imageUploadProgress, setImageUploadProgress] = useState(0);
    const [uploadingImages, setUploadingImages] = useState(false);

    // Google Maps state
    const [map, setMap] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState({
        lat: 19.4326, // Default to Mexico City
        lng: -99.1332,
    });
    const [mapLoaded, setMapLoaded] = useState(false);

    const mapContainerStyle = {
        width: "100%",
        height: "400px",
    };

    useEffect(() => {
        // Get user's current location if available
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const userLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setCurrentLocation(userLocation);
                    setSelectedLocation(userLocation);
                },
                (error) => {
                    console.error("Error getting current location:", error);
                }
            );
        }
    }, []);

    // Generate slug from name
    useEffect(() => {
        if (formData.name && !slugTouched) {
            const generatedSlug = formData.name
                .toLowerCase()
                .trim()
                .replace(/[^\w\s-]/g, "") // Remove special characters except spaces and hyphens
                .replace(/\s+/g, "-") // Replace spaces with hyphens
                .replace(/-+/g, "-"); // Replace multiple hyphens with a single one

            setFormData(prev => ({
                ...prev,
                slug: generatedSlug
            }));

            // Validate the generated slug
            if (generatedSlug) {
                validateSlug(generatedSlug);
            }
        }
    }, [formData.name]);

    // Validate slug
    const validateSlug = async (slug) => {
        if (!slug) {
            setSlugError("El slug es requerido");
            setSlugAvailable(false);
            return false;
        }

        // Check if the slug format is valid
        const slugRegex = /^[a-z0-9-]+$/;
        if (!slugRegex.test(slug)) {
            setSlugError("El slug solo puede contener letras minúsculas, números y guiones");
            setSlugAvailable(false);
            return false;
        }

        setCheckingSlug(true);
        setSlugAvailable(null);

        try {
            // Check if the slug is already in use
            const placesCollection = collection(db, "places");
            const q = query(placesCollection, where("slug", "==", slug));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                setSlugError("");
                setSlugAvailable(true);
                setCheckingSlug(false);
                return true;
            } else {
                setSlugError("Este slug ya está en uso. Por favor, elige otro.");
                setSlugAvailable(false);
                setCheckingSlug(false);
                return false;
            }
        } catch (error) {
            console.error("Error validando slug:", error);
            setSlugError("Error al validar el slug");
            setSlugAvailable(false);
            setCheckingSlug(false);
            return false;
        }
    };

    // Handle form input changes
    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // If slug is changed manually, validate it and mark as touched
        if (name === "slug") {
            setSlugTouched(true);
            await validateSlug(value);
        }
    };

    // Handle opening hours changes
    const handleHoursChange = (day, field, value) => {
        setFormData((prev) => ({
            ...prev,
            openingHours: {
                ...prev.openingHours,
                [day]: {
                    ...prev.openingHours[day],
                    [field]: field === "closed" ? !prev.openingHours[day].closed : value,
                },
            },
        }));
    };

    // Handle category selection (ahora delegado a CategoryManager)
    const handleCategoryChange = (categoryId) => {
        const updatedCategories = formData.categories.includes(categoryId)
            ? formData.categories.filter((id) => id !== categoryId)
            : [...formData.categories, categoryId];

        setFormData((prev) => ({
            ...prev,
            categories: updatedCategories,
        }));
    };

    // Handle categories loaded from CategoryManager
    const handleCategoriesLoaded = (categories) => {
        setAllCategories(categories);
    };

    // Handle image upload
    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);

        if (files.length + images.length > 5) {
            setError("Máximo 5 imágenes permitidas");
            return;
        }

        // Preview images
        const newImages = files.map((file) => ({
            file,
            preview: URL.createObjectURL(file),
            uploaded: false,
            url: null,
        }));

        setImages((prev) => [...prev, ...newImages]);
    };

    // Remove image
    const handleRemoveImage = (index) => {
        const newImages = [...images];

        // Revoke object URL to avoid memory leaks
        if (newImages[index].preview) {
            URL.revokeObjectURL(newImages[index].preview);
        }

        newImages.splice(index, 1);
        setImages(newImages);
    };

    // Upload images to Firebase Storage
    const uploadImages = async () => {
        if (images.length === 0) return [];

        const uploadPromises = images
            .filter((img) => !img.uploaded) // Only upload images that haven't been uploaded
            .map((image) => {
                return new Promise((resolve, reject) => {
                    const storageRef = ref(
                        storage,
                        `places/${user.uid}/${Date.now()}_${image.file.name}`
                    );

                    const uploadTask = uploadBytesResumable(storageRef, image.file);

                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            const progress = Math.round(
                                (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                            );
                            setImageUploadProgress(progress);
                        },
                        (error) => {
                            console.error("Error uploading image:", error);
                            reject(error);
                        },
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve(downloadURL);
                        }
                    );
                });
            });

        setUploadingImages(true);
        try {
            const uploadedUrls = await Promise.all(uploadPromises);

            // Update the images state with uploaded URLs
            const updatedImages = [...images];
            let urlIndex = 0;

            for (let i = 0; i < updatedImages.length; i++) {
                if (!updatedImages[i].uploaded) {
                    updatedImages[i].url = uploadedUrls[urlIndex];
                    updatedImages[i].uploaded = true;
                    urlIndex++;
                }
            }

            setImages(updatedImages);
            setUploadingImages(false);
            return updatedImages.map((img) => img.url).filter(url => url);
        } catch (error) {
            setUploadingImages(false);
            throw error;
        }
    };

    // Handle map click to set marker position
    const handleMapClick = (event) => {
        setSelectedLocation({
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
        });
    };

    // Handle map load
    const handleMapLoad = (mapInstance) => {
        setMap(mapInstance);
        setMapLoaded(true);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user) {
            setError("Debe iniciar sesión para crear un restaurante");
            return;
        }

        // Basic validation
        if (!formData.name || !formData.address || formData.categories.length === 0) {
            setError("Por favor complete los campos obligatorios: nombre, dirección y categorías");
            return;
        }

        // Validate slug
        const isSlugValid = await validateSlug(formData.slug);
        if (!isSlugValid) {
            setError("Por favor, corrige el slug antes de continuar");
            return;
        }

        setLoading(true);
        setError("");

        try {
            // Upload images first
            const imageUrls = await uploadImages();

            // Create the place document
            const placesCollection = collection(db, "places");

            const newPlace = {
                name: formData.name,
                description: formData.description,
                address: formData.address,
                phone: formData.phone,
                email: formData.email,
                website: formData.website,
                slug: formData.slug,
                categories: formData.categories,
                openingHours: formData.openingHours,
                location: {
                    lat: selectedLocation.lat,
                    lng: selectedLocation.lng,
                },
                images: imageUrls,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                ownerId: user.uid,
                status: "active",
            };

            await addDoc(placesCollection, newPlace);

            setSuccess(true);

            // Redirect after short delay
            setTimeout(() => {
                navigate("/dashboard");
            }, 2000);
        } catch (error) {
            console.error("Error creating place:", error);
            setError("Error al crear el restaurante. Por favor, inténtelo de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    // Handle form reset
    const handleReset = () => {
        setFormData({
            name: "",
            description: "",
            address: "",
            phone: "",
            email: "",
            website: "",
            slug: "",
            categories: [],
            openingHours: {
                monday: { open: "08:00", close: "22:00", closed: false },
                tuesday: { open: "08:00", close: "22:00", closed: false },
                wednesday: { open: "08:00", close: "22:00", closed: false },
                thursday: { open: "08:00", close: "22:00", closed: false },
                friday: { open: "08:00", close: "22:00", closed: false },
                saturday: { open: "10:00", close: "23:00", closed: false },
                sunday: { open: "10:00", close: "18:00", closed: false },
            },
        });
        setImages([]);
        setSelectedLocation({
            lat: currentLocation ? currentLocation.lat : 19.4326,
            lng: currentLocation ? currentLocation.lng : -99.1332,
        });
        setError("");
        setSuccess(false);
        setSlugError("");
        setSlugAvailable(null);
        setSlugTouched(false);
    };

    // Days of the week translated to Spanish
    const daysOfWeek = [
        { id: "monday", label: "Lunes" },
        { id: "tuesday", label: "Martes" },
        { id: "wednesday", label: "Miércoles" },
        { id: "thursday", label: "Jueves" },
        { id: "friday", label: "Viernes" },
        { id: "saturday", label: "Sábado" },
        { id: "sunday", label: "Domingo" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 pt-20 pb-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-8 text-center">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                        Registrar Nuevo Restaurante
                    </h1>
                    <p className="mt-2 text-sm sm:text-base text-gray-600">
                        Complete la información para registrar su restaurante en Vine Business
                    </p>
                </div>

                {/* Success Message */}
                {success && (
                    <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg
                                    className="h-5 w-5 text-green-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-green-700">
                                    Restaurante registrado exitosamente. Redirigiendo...
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-5 w-5 text-red-400" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Basic Information */}
                        <div className="md:col-span-2">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                                Información Básica
                            </h2>
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Nombre del Restaurante *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                            />
                        </div>

                        {/* Slug */}
                        <div>
                            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                <LinkIcon className="h-4 w-4 mr-1 text-gray-500" />
                                URL del Restaurante *
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500">vine.mx/go/</span>
                                </div>
                                <input
                                    type="text"
                                    id="slug"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="tu-restaurante"
                                    className={`block w-full pl-[140px] pr-10 rounded-md border-gray-300 shadow-sm focus:ring-2 sm:text-sm ${slugError
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                                        : "focus:border-purple-500 focus:ring-purple-500"
                                        }`}
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    {checkingSlug ? (
                                        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                                    ) : slugAvailable ? (
                                        <Check className="w-5 h-5 text-green-500" />
                                    ) : slugError ? (
                                        <X className="w-5 h-5 text-red-500" />
                                    ) : null}
                                </div>
                            </div>
                            {slugError ? (
                                <p className="mt-1 text-sm text-red-600">{slugError}</p>
                            ) : (
                                <p className="mt-1 text-xs text-gray-500">
                                    Solo letras minúsculas, números y guiones. Sin espacios ni caracteres especiales.
                                </p>
                            )}
                        </div>

                        {/* Phone */}
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                Teléfono
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                            />
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                            />
                        </div>

                        {/* Website */}
                        <div>
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                                Sitio Web
                            </label>
                            <input
                                type="url"
                                id="website"
                                name="website"
                                value={formData.website}
                                onChange={handleInputChange}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="https://www.example.com"
                            />
                        </div>

                        {/* Address */}
                        <div className="md:col-span-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                Dirección *
                            </label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                            />
                        </div>

                        {/* Description */}
                        <div className="md:col-span-2">
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                                Descripción
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={4}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                placeholder="Describe tu restaurante, especialidades, ambiente, etc."
                            />
                        </div>

                        {/* Categories - Ahora usando el componente CategoryManager */}
                        <div className="md:col-span-2">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                                Categorías *
                            </h2>

                            <CategoryManager
                                selectedCategories={formData.categories}
                                onCategorySelect={handleCategoryChange}
                                onCategoriesLoad={handleCategoriesLoaded}
                            />
                        </div>

                        {/* Images */}
                        <div className="md:col-span-2">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                                Imágenes (máx. 5)
                            </h2>

                            <div className="mb-4">
                                <label
                                    htmlFor="images"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Fotografías del restaurante
                                </label>

                                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-purple-500 transition-colors">
                                    <div className="space-y-1 text-center">
                                        <Camera className="mx-auto h-12 w-12 text-gray-400" />
                                        <div className="flex text-sm text-gray-600">
                                            <label
                                                htmlFor="images"
                                                className="relative cursor-pointer rounded-md font-medium text-purple-600 hover:text-purple-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-purple-500"
                                            >
                                                <span>Subir archivos</span>
                                                <input
                                                    id="images"
                                                    name="images"
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handleImageChange}
                                                    className="sr-only"
                                                    disabled={images.length >= 5 || uploadingImages}
                                                />
                                            </label>
                                            <p className="pl-1">o arrastre y suelte</p>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            PNG, JPG, GIF hasta 10MB
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Image Previews */}
                            {images.length > 0 && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mt-4">
                                    {images.map((image, index) => (
                                        <div
                                            key={index}
                                            className="relative border rounded-md overflow-hidden group"
                                        >
                                            <img
                                                src={image.preview || image.url}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-24 object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                            {image.uploaded && (
                                                <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs text-center py-1">
                                                    Subida
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Upload Progress */}
                            {uploadingImages && (
                                <div className="mt-2">
                                    <div className="relative pt-1">
                                        <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-purple-200">
                                            <div
                                                style={{ width: `${imageUploadProgress}%` }}
                                                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500 transition-all duration-300"
                                            ></div>
                                        </div>
                                        <div className="text-xs text-purple-600">
                                            {imageUploadProgress}% Completado
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Location */}
                        <div className="md:col-span-2">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                                Ubicación
                            </h2>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Selecciona la ubicación en el mapa (haz click para marcar)
                                </label>

                                <div className="h-96 rounded-lg overflow-hidden border border-gray-300">
                                    <LoadScript
                                        googleMapsApiKey="AIzaSyBSXE8zZ-SqMWdDz16hr00Djvkm3mMit28"
                                        onLoad={() => console.log("Google Maps script loaded")}
                                    >
                                        <GoogleMap
                                            mapContainerStyle={mapContainerStyle}
                                            center={selectedLocation}
                                            zoom={15}
                                            onClick={handleMapClick}
                                            onLoad={handleMapLoad}
                                        >
                                            {selectedLocation && <Marker position={selectedLocation} />}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>

                                {selectedLocation && (
                                    <div className="mt-2 text-sm text-gray-600 flex items-center">
                                        <MapPin className="h-4 w-4 mr-1 text-purple-500" />
                                        <span>
                                            Lat: {selectedLocation.lat.toFixed(6)}, Lng:{" "}
                                            {selectedLocation.lng.toFixed(6)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Opening Hours */}
                        <div className="md:col-span-2">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                                Horario de Atención
                            </h2>

                            <div className="space-y-4">
                                {daysOfWeek.map((day) => (
                                    <div key={day.id} className="flex items-center space-x-4">
                                        <div className="w-28">
                                            <span className="text-sm font-medium text-gray-700">
                                                {day.label}
                                            </span>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <label className="inline-flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.openingHours[day.id].closed}
                                                    onChange={() =>
                                                        handleHoursChange(day.id, "closed", null)
                                                    }
                                                    className="rounded border-gray-300 text-purple-600 shadow-sm focus:border-purple-300 focus:ring focus:ring-purple-200 focus:ring-opacity-50"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">
                                                    Cerrado
                                                </span>
                                            </label>
                                        </div>

                                        {!formData.openingHours[day.id].closed && (
                                            <>
                                                <div className="flex items-center">
                                                    <span className="text-sm text-gray-500 mr-2">Abre a las</span>
                                                    <input
                                                        type="time"
                                                        value={formData.openingHours[day.id].open}
                                                        onChange={(e) =>
                                                            handleHoursChange(
                                                                day.id,
                                                                "open",
                                                                e.target.value
                                                            )
                                                        }
                                                        className="block rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                                    />
                                                </div>
                                                <div className="flex items-center">
                                                    <span className="text-sm text-gray-500 mr-2">Cierra a las</span>
                                                    <input
                                                        type="time"
                                                        value={formData.openingHours[day.id].close}
                                                        onChange={(e) =>
                                                            handleHoursChange(
                                                                day.id,
                                                                "close",
                                                                e.target.value
                                                            )
                                                        }
                                                        className="block rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="md:col-span-2 mt-6 pt-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="w-full sm:w-auto px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading || uploadingImages || !slugAvailable}
                                className="w-full sm:w-auto px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {loading || uploadingImages ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <Save className="mr-2 h-4 w-4" />
                                        Registrar Restaurante
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>

                {/* URL Preview */}
                {formData.slug && slugAvailable && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="text-sm font-medium text-gray-800 mb-2 flex items-center">
                            <LinkIcon className="h-4 w-4 mr-2 text-purple-600" />
                            Previsualización de URL:
                        </h3>
                        <p className="text-sm text-purple-600 font-medium">
                            vine.mx/go/{formData.slug}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Esta será la URL pública de tu restaurante en Vine Business.
                        </p>
                    </div>
                )}

                {/* Help Box */}
                <div className="mt-8 bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h3 className="text-sm font-medium text-blue-800 mb-1">
                        ¿Por qué registrar tu restaurante?
                    </h3>
                    <ul className="text-sm text-blue-700 space-y-1 ml-4">
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Optimiza tu inventario y reduce costos</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Accede a precios mayoristas exclusivos</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Automatiza los pedidos a proveedores</span>
                        </li>
                        <li className="flex items-start">
                            <span className="mr-2">•</span>
                            <span>Recibe reportes de rendimiento y análisis</span>
                        </li>
                    </ul>
                    <p className="text-sm text-blue-800 mt-2">
                        Al registrar tu restaurante, te ayudamos a gestionar eficientemente tu negocio.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NewPlace;